import { useCallback, useEffect, useMemo } from 'react';
import { useStore } from 'src/contexts/StoreContext';
import { DRAWER_POSITIONS, DRAWER_VIEWS } from 'src/store/drawer/drawer.constants';
import { ICON_NAMES } from '../icon/icon.helper';
import { icon } from 'src/theme/colors';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { useTypedRoute } from 'src/editor-v3/hooks/useTypedRoute';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { useTypedNavigation } from 'src/editor-v3/hooks/useTypedNavigation';

export const useToastSharePhotos = () => {
  const {
    i18n,
    drawer,
    data,
    toastStore,
    project: {
      activeGroup,
      startAddingPhotosFlow,
      activeProject,
      productConfigurationId,
      groupCode,
      setShowUploadPhotosBanner,
      addProductsV2,
    },
  } = useStore();

  const navigation = useTypedNavigation();
  const route = useTypedRoute<ROUTE_NAMES.Upload>();
  const params = route.params ?? {};
  const { groupCodeExternal, skipConfigurationStep, skipStepValue, ...filteredParams } = params;
  const isUploadRoute = useMemo(
    () => [ROUTE_NAMES.Upload, ROUTE_NAMES.EditorNew].includes(route?.name as ROUTE_NAMES),
    [route.name],
  );

  useEffect(() => {
    setShowUploadPhotosBanner(false);
    amplitude.trackEvent(AnalyticsEvents.sharePhoto.addedView, {
      position: isUploadRoute ? 'picker' : 'personalize',
    });
  }, []);

  const onPressConfirm = useCallback(async () => {
    drawer.openDrawer({
      drawerPosition: DRAWER_POSITIONS.Left,
      drawerView: DRAWER_VIEWS.PhotoDrawer,
      withBackdrop: false,
      openPoint: 'link',
      uploadButtonCallback: async () => {
        if (isUploadRoute) {
          const id = await activeProject?.getDefaultProductIdByGroup(activeGroup);
          activeGroup?.checkMinMaxProductsAndNotify();
          addProductsV2({
            ids: activeProject?.selectedThumbnails.map(t => t.originalImageId) ?? [],
            productConfigurationId:
              toastStore.data || id || activeGroup?.currectProductConfigurationId || productConfigurationId,
            groupCode: activeGroup?.code || groupCode,
            groupId: activeGroup?.groupIdSDK,
          });
          toastStore.setData(undefined);
        } else {
          startAddingPhotosFlow({
            initialProductConfigurationId: activeGroup
              ? activeGroup.productConfigurationIdDefault
              : productConfigurationId,
            groupCode: activeGroup ? activeGroup.code : groupCode,
            fileIds: activeProject?.selectedThumbnails.map(t => t.originalImageId),
          });
        }
        drawer.closeDrawer();
      },
    });
    data.setShowNewImageNotification(false);

    amplitude.trackEvent(AnalyticsEvents.sharePhoto.addedClick, {
      action: 'accept',
      position: isUploadRoute ? 'picker' : 'personalize',
    });
    if (isUploadRoute) navigation.navigate(ROUTE_NAMES.EditorNew, { ...filteredParams });
  }, []);

  const onPressDecline = useCallback(async () => {
    toastStore.showInfoToast({
      message: i18n.t('toast:photos-added'),
      iconColor: icon.iconPrimary,
      id: 'toast-photos-added-to-drawer',
      removeOnPress: false,
      onPress: () => {},
      icon: ICON_NAMES.PhotoAlbum,
      duration: 3000,
    });
    data.setShowNewImageNotification(false);
    amplitude.trackEvent(AnalyticsEvents.sharePhoto.addedClick, {
      action: 'refuse',
      position: isUploadRoute ? 'picker' : 'personalize',
    });
  }, []);

  return { i18n, data, onPressConfirm, onPressDecline };
};
